import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Send, Loader, PlusCircle, RefreshCw, Check, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { AuthContext } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import { ModalsContext } from '../context/ModalsContext';
import { useGoogleAds } from '../../hooks/useGoogleAds';
import { useMetaAds } from '../../hooks/useMetaAds';
import { Message, QuickOptions, CampaignPreviewSection, PreviewHeader, EsporaiTooltip } from 'components';
import { quickOptions } from './QuickOptions';
import ThinkingIndicator from './ThinkingIndicator';
import { chatsApi, logApi } from '../../api';
import MobileTopNavBar from 'components/MobileTopNavBar';
import SuccessAnimation from '../SuccessAnimation';
const API_URL = process.env.REACT_APP_API_URL;

const { createLog } = logApi;

const ERROR_PROCESSING_MESSAGE = '❌ Error procesando el mensaje. Por favor, intenta nuevamente.';

const AICampaignCreation = () => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [deviceView, _setDeviceView] = useState('desktop');
  const [activeTab, setActiveTab] = useState('google');
  const [showQuickOptions, setShowQuickOptions] = useState(true);
  const [loadingResponse, setLoadingResponse] = useState(false);
  const [isCreatingCampaign, setIsCreatingCampaign] = useState(false);
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const [_currentChatTitle, setCurrentChatTitle] = useState('');
  const [loadingChat, setLoadingChat] = useState(false);
  const [showThinkingProcess, setShowThinkingProcess] = useState(false);
  const [isGoogleValid, setIsGoogleValid] = useState(true);
  const chatRef = useRef(null);
  const inputRef = useRef(null);
  const thinkingRef = useRef(null);
  const [_chatReady, setChatReady] = useState(false);
  
  // Success animation state
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [successCampaignData, setSuccessCampaignData] = useState(null);

  const { auth, hasGoogleAdsConnected, hasMetaConnected } = useContext(AuthContext);
  const { currentCampaign, fetchCampaign, setCurrentCampaign } = useChat();
  const { openPlansModal, setPlansModalMessage } = useContext(ModalsContext);
  const { connectGoogleAds } = useGoogleAds();
  const { startMetaOAuth } = useMetaAds();
  const navigate = useNavigate();

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const chatId = params.get('chat');
  const initialMessage = params.get('initialMessage');
  const [localChatId, setLocalChatId] = useState(chatId);

  // Keep localChatId in sync with the URL's chat param
  useEffect(() => {
    if (chatId && chatId !== localChatId) {
      setLocalChatId(chatId);
    }
  }, [chatId, localChatId]);

  // Initialize welcome message when no chat is specified
  useEffect(() => {
    if (!chatId && !initialMessage) {
      setMessages([{
        role: 'assistant',
        content: '¡Hola! Soy tu asistente de IA para crear campañas publicitarias. ¿En qué puedo ayudarte hoy?',
        timestamp: new Date().toISOString()
      }]);
      setShowQuickOptions(true);
      setChatReady(true);
    }
  }, [chatId, initialMessage]);

  const newChat = useCallback(async (title, initialMessage) => {
    const sessionResponse = await chatsApi.createChatSession({
      title,
      no_initial_message: initialMessage
    });
    const createdSessionId = sessionResponse.data.id;
    setLocalChatId(createdSessionId);
    // Dispatch event for sidebar update
    const newSessionEvent = new CustomEvent('newChatSessionCreated', {
      detail: {
        sessionId: createdSessionId,
        sessionData: {
          id: createdSessionId,
          title,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      }
    });
    window.dispatchEvent(newSessionEvent);
    // Update URL to real chatId and remove initialMessage
    const updatedParams = new URLSearchParams(
      typeof window !== 'undefined' ? window.location.search : ''
    );
    updatedParams.set('chat', createdSessionId);
    updatedParams.delete('initialMessage');
    navigate(`/campaign-creation?${updatedParams.toString()}`, { replace: true });
    // 2. Send the initial message as a user message
    if (initialMessage) {
      // Check if this is a campaign creation message that should trigger thinking process
      const isCampaignMessage = quickOptions.some(option => option.message === initialMessage);
      
      const userMessage = {
        role: 'user',
        content: initialMessage,
        timestamp: new Date().toISOString()
      };
      setMessages([userMessage]);
      setShowQuickOptions(false);
      
      if (isCampaignMessage) {
        setShowThinkingProcess(true);
      } else {
        setIsTyping(true);
      }
      
      setLoadingResponse(true);
      // Actually send the message to the backend
      const aiResponse = await chatsApi.postCampaignConversationMessage(createdSessionId, {
        message: initialMessage
      });
      const messageContent = aiResponse.data.message;
      
      // If thinking process is active, complete it before showing response
      if (isCampaignMessage && thinkingRef.current) {
        thinkingRef.current.complete();
      }
      // 3. Fetch campaign after message is sent
      await fetchCampaign(createdSessionId);
      // Add assistant message
      const assistantMessage = {
        role: 'assistant',
        content: messageContent,
        timestamp: new Date().toISOString(),
      };
      setMessages([userMessage, assistantMessage]);
      setIsTyping(false);
      setLoadingResponse(false);
      setIsCreatingCampaign(false);
      setShowThinkingProcess(false);
    } else {
      setMessages([{
        role: 'assistant',
        content: '¡Hola! Soy tu asistente de IA para crear campañas publicitarias. ¿En qué puedo ayudarte hoy?',
        timestamp: new Date().toISOString()
      }]);
      setShowQuickOptions(true);
      setIsTyping(false);
      setLoadingResponse(false);
      setIsCreatingCampaign(false);
    }
  }, [navigate, fetchCampaign]);

  const loadExistingChat = useCallback(async (sessionId) => {
    if (!auth.token || !sessionId) return;
    setCurrentCampaign(null);
    setLoadingChat(true);
    setMessages([]);
    setChatReady(false);
    
    try {
      const response = await chatsApi.getChatSession(sessionId);

      if (!response?.data?.messages?.length) {
        setMessages([{
          role: 'assistant',
          content: '¡Hola! Soy tu asistente de IA para crear campañas publicitarias. ¿En qué puedo ayudarte hoy?',
          timestamp: new Date().toISOString()
        }]);
        setShowQuickOptions(true);
        setChatReady(true);
        await fetchCampaign(sessionId);
        return;
      }

      const formattedMessages = response.data.messages.map(msg => ({
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp
      }));

      const sortedMessages = [...formattedMessages].sort((a, b) =>
        new Date(a.timestamp) - new Date(b.timestamp)
      );

      setMessages(sortedMessages);
      setCurrentChatTitle(response.data.title || 'Chat');

      const shouldShowQuickOptions = sortedMessages.length === 1 &&
        sortedMessages[0].role === 'assistant';
      setShowQuickOptions(shouldShowQuickOptions);
      setChatReady(true);
      await fetchCampaign(sessionId);

    } catch (error) {
      console.error('Error loading chat session:', error);
      setChatReady(true);
    } finally {
      setLoadingChat(false);
    }
  }, [auth.token, fetchCampaign, setCurrentCampaign]);

  // Main effect: handle chat creation or loading
  useEffect(() => {
    if (localChatId === 'tmp_id' && initialMessage) {
      setCurrentCampaign(null);
      setMessages([]);
      setChatReady(false);
      setShowQuickOptions(true);
      setIsTyping(false);
      setLoadingResponse(false);
      setIsCreatingCampaign(false);
      setShowMobilePreview(false);
      // Create a new chat session, then send the initial message
      (async () => {
        setLoadingChat(true);
        setShowMobilePreview(false);
        try {
          // 1. Create the chat session (no initial message)
          const isGoogleCampaign = initialMessage.toLowerCase().includes('google');
          const isMetaCampaign = initialMessage.toLowerCase().includes('meta');
          const isWhatsappCampaign = initialMessage.toLowerCase().includes('whatsapp');
          const today = new Date().toLocaleDateString();
          const defaultTitle = `Chat ${today}`;
          const title = isGoogleCampaign ? `Campaña en Google ${today}` : isMetaCampaign ? `Campaña en Meta ${today}` : isWhatsappCampaign ? `Campaña en Whatsapp ${today}` : defaultTitle;
          await newChat(title, initialMessage);
        } catch (error) {
          console.error('Error creating chat and sending initial message:', error);
          setShowQuickOptions(true);
          setIsTyping(false);
          setLoadingResponse(false);
          setIsCreatingCampaign(false);
        } finally {
          setLoadingChat(false);
        }
      })();
    } else if (localChatId && localChatId !== 'tmp_id') {
      // Load existing chat
      loadExistingChat(localChatId);
    }
  }, [localChatId, initialMessage, loadExistingChat, newChat, setCurrentCampaign]);

  // Set active tab based on campaign provider
  useEffect(() => {
    if (currentCampaign?.provider?.toLowerCase().includes('meta')) {
      setActiveTab('meta');
    } else {
      setActiveTab('google');
    }
  }, [currentCampaign, currentCampaign?.name]);

  // Hide mobile preview on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024 && currentCampaign?.name) {
        setShowMobilePreview(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentCampaign?.name]);

  // Hide quick options after first interaction
  useEffect(() => {
    if (messages.length > 1 || isTyping) {
      const t = setTimeout(() => setShowQuickOptions(false), 300);
      return () => clearTimeout(t);
    }
  }, [messages.length, isTyping]);

  // Autofocus input after assistant reply
  useEffect(() => {
    if (!isTyping && messages.length && messages[messages.length - 1].role === 'assistant') {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [isTyping, messages]);

  // Scroll chat to bottom on new messages
  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages]);

  // Whenever localChatId changes, mark chat as not ready until loaded
  useEffect(() => {
    setChatReady(false);
  }, [localChatId]);

  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const handleQuickOptionSelect = async (text) => {
    // Check if this is a campaign creation message that should trigger thinking process
    const isCampaignMessage = quickOptions.some(option => option.message === text);
    
    await setShowQuickOptions(false);
    await setInputValue(text);
    await sleep(100);
    
    if (isCampaignMessage) {
      setShowThinkingProcess(true);
    }
    
    await handleSendMessage({ preventDefault: () => { } }, text);
  };

  const handleSendMessage = async (e, text) => {
    e.preventDefault();
    const textInput = text || inputValue.trim();

    if (!textInput || isTyping) {
      return;
    }

    // If no valid chat session exists, create one first
    let currentChatId = localChatId;
    if (!currentChatId || currentChatId === 'tmp_id') {
      try {
        setLoadingResponse(true);
        const today = new Date().toLocaleDateString();
        const title = `Chat ${today}`;
        
        const sessionResponse = await chatsApi.createChatSession({
          title,
          no_initial_message: null
        });
        
        currentChatId = sessionResponse.data.id;
        setLocalChatId(currentChatId);
        
        // Update URL with the new chat ID
        const updatedParams = new URLSearchParams(location.search);
        updatedParams.set('chat', currentChatId);
        navigate(`/campaign-creation?${updatedParams.toString()}`, { replace: true });
        
        // Dispatch event for sidebar update
        const newSessionEvent = new CustomEvent('newChatSessionCreated', {
          detail: {
            sessionId: currentChatId,
            sessionData: {
              id: currentChatId,
              title,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            }
          }
        });
        window.dispatchEvent(newSessionEvent);
      } catch (error) {
        console.error('Error creating chat session:', error);
        toast.error('Error al crear la sesión de chat. Por favor, intenta nuevamente.');
        setLoadingResponse(false);
        return;
      }
    }

    const newMessage = {
      role: 'user',
      content: textInput,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, newMessage]);
    
    setInputValue('');
    
    setShowQuickOptions(false);
    
    // Only show thinking process if it was triggered by campaign message
    if (!showThinkingProcess) {
      setIsTyping(true);
    }
    setLoadingResponse(true);

    let aiResponse;
    try {
      // Otherwise, send message as usual
      aiResponse = await chatsApi.postCampaignConversationMessage(currentChatId, {
        message: textInput,
        campaign_preview: currentCampaign
      });

      if (!aiResponse?.data?.message) {
        throw new Error('No se obtuvo respuesta del servidor');
      }
    } catch (error) {
      console.error('Error al enviar mensaje de chat:', error);
      setMessages(prev => [...prev, {
        role: 'system',
        content: ERROR_PROCESSING_MESSAGE,
        timestamp: new Date().toISOString()
      }]);
      setIsTyping(false);
      setLoadingResponse(false);
      createLog({
        level: 'error',
        message: `Usuario ${auth.user.email} envió mensaje de chat y obtuvo un error: ${error.message}`,
      });
    }

    try {
      // Check if response contains JSON
      if (!aiResponse) {
        return;
      }
      const messageContent = aiResponse.data.message;

      // If thinking process is active, complete it before showing response
      if (showThinkingProcess && thinkingRef.current) {
        thinkingRef.current.complete();
      }

      await fetchCampaign(currentChatId);

      // Process AI response
      const assistantMessage = {
        role: 'assistant',
        content: messageContent,
        timestamp: new Date().toISOString(),
      };

      setMessages(prev => {
        const newMessages = [...prev, assistantMessage];
        return newMessages;
      });

    } catch (error) {
      console.error('Error:', error);

    } finally {
      setIsTyping(false);
      setLoadingResponse(false);
      setShowThinkingProcess(false);
    }
  };

  // Use localChatId for campaign confirm and preview
  const handleCampaignConfirm = async () => {
    if (!currentCampaign) return;
  
    try {
      if (currentCampaign.provider === 'google' && !hasGoogleAdsConnected) {
        createLog({
          level: 'info',
          message: `Click en "Publicar" campaña en Google (${currentCampaign.campaign_type}) pero no se pudo publicar porque no se tiene conectado Google Ads`,
          channel: "notificaciones-campañas"
        });
        connectGoogleAds(false);
        return;
      }

      if (currentCampaign.provider === 'meta' && !hasMetaConnected) {
        createLog({
          level: 'info',
          message: `Click en "Publicar" campaña en Meta (${currentCampaign.campaign_type}) pero no se pudo publicar porque no se tiene conectado Meta Ads`,
          channel: "notificaciones-campañas"
        });
        startMetaOAuth(false);
        return;
      }

      setIsCreatingCampaign(true);

      try {
        const campaignResponse = await chatsApi.sendCampaign(localChatId);
        const providerId = campaignResponse?.data?.v2_campaign?.provider_campaign_id;
        
        // Set success animation data
        setSuccessCampaignData({
          provider: currentCampaign.provider,
          providerId: providerId
        });
        
        // Show success animation
        setShowSuccessAnimation(true);
      } catch (error) {
        if (error?.response?.data?.error_status === "MAX_CAMPAIGNS_REACHED") {
          createLog({
            level: 'info',
            message: `Click en "Publicar" campaña de ${currentCampaign.provider} (${currentCampaign.campaign_type}) pero no se pudo publicar porque se alcanzó el límite de campañas para el plan actual`,
            channel: "notificaciones-campañas"
          });
          setPlansModalMessage(error.response.data.error);
          openPlansModal();
          return;
        }
        toast.error(`Error al crear la campaña. ${error.response.data.error}`);
        console.error("Error creating campaign:", error);
      }
    } catch (error) {
      // In case of error, just log to console but SHOW SUCCESS MESSAGE anyway
      console.error("General error (ignored):", error);
  
      // ALWAYS show success message, even in case of general error
      const successMessage = {
        role: 'system',
        content: '✅ Campaña creada con éxito!',
        timestamp: new Date().toISOString()
      };
      
      // Añadir mensaje y actualizar backups
      setMessages(prev => {
        const newMessages = [...prev, successMessage];
        return newMessages;
      });
    } finally {
      setIsCreatingCampaign(false);
    }
  };

  /**
   * Handles the completion of success animation
   */
  const handleSuccessAnimationComplete = () => {
    setShowSuccessAnimation(false);
    setSuccessCampaignData(null);
  };

  const isCampaignSent = Boolean(currentCampaign?.provider_campaign_id);

  // Campaign readiness logic
  const isCampaignReady = Boolean(currentCampaign?.name && currentCampaign?.provider);
  const prevIsCampaignReady = useRef(isCampaignReady);

  // Computed function to determine if publish button should be disabled
  const isPublishButtonDisabled = () => {
    // If campaign is already sent, disable
    if (isCampaignSent) return true;
    
    // If campaign is not ready, disable
    if (!isCampaignReady) return true;
    
    // If currently creating campaign, disable
    if (isCreatingCampaign) return true;
    
    // For Meta campaigns, check if media is required
    if (currentCampaign?.provider?.toLowerCase().includes('meta')) {
      const hasImages = currentCampaign?.ad_content?.media?.images?.length > 0;
      const hasVideos = currentCampaign?.ad_content?.media?.videos?.length > 0;
      const hasCatalog = currentCampaign?.catalog?.products?.length > 0;
      return !hasImages && !hasVideos && !hasCatalog;
    }
    
    // For Google campaigns, check validation
    if (currentCampaign?.provider?.toLowerCase().includes('google')) {
      return !isGoogleValid;
    }
    
    return false;
  };

  // Get the appropriate title for the disabled button
  const getDisabledButtonTitle = () => {
    if (isCampaignSent) return "Esta campaña ya fue creada";
    
    if (!isCampaignReady) return "La campaña no está lista";
    
    if (isCreatingCampaign) return "Creando campaña...";
    
    if (currentCampaign?.provider?.toLowerCase().includes('meta')) {
      const hasImages = currentCampaign?.ad_content?.media?.images?.length > 0;
      const hasVideos = currentCampaign?.ad_content?.media?.videos?.length > 0;
      const hasCatalog = currentCampaign?.catalog?.products?.length > 0;
      if (!hasImages && !hasVideos && !hasCatalog) {
        return "Debes subir al menos una imagen o video, o conectar un catálogo";
      }
    }
    
    if (currentCampaign?.provider?.toLowerCase().includes('google') && !isGoogleValid) {
      return "Para campañas Performance Max, debes subir un logo y al menos una imagen o video";
    }
    
    return "";
  };

  const isMobile = window.innerWidth < 700;
  useEffect(() => {
    if (
      isMobile &&
      !prevIsCampaignReady.current &&
      isCampaignReady
    ) {
      setShowMobilePreview(true);
    }
    prevIsCampaignReady.current = isCampaignReady;
  }, [isCampaignReady, deviceView, isMobile]);

  // Auto-expand textarea up to 4 lines
  const handleInputResize = (e) => {
    const textarea = e.target;
    textarea.style.height = 'auto';
    const lineHeight = 24;
    const maxHeight = 4 * lineHeight;
    textarea.style.height = Math.min(textarea.scrollHeight, maxHeight) + 'px';
  };

  const handleNewChat = () => {
    // Navigate to clean URL first
    navigate('/campaign-creation', { replace: true });
    
    setCurrentCampaign(null);
    setMessages([{
      role: 'assistant',
      content: '¡Hola! Soy tu asistente de IA para crear campañas publicitarias. ¿En qué puedo ayudarte hoy?',
      timestamp: new Date().toISOString()
    }]);
    setLocalChatId(null);
    setChatReady(true);
    setIsTyping(false);
    setLoadingResponse(false);
    setIsCreatingCampaign(false);
    setShowMobilePreview(false);
    setShowQuickOptions(true);
  };

  return (
    <div className="h-full pb-12 md:pb-0 flex flex-col overflow-hidden bg-white md:bg-[#F2F2F2] ">
      <MobileTopNavBar
        onBack={showMobilePreview ? () => setShowMobilePreview(false) : () => navigate('/home')}
      >
        <EsporaiTooltip
          content={getDisabledButtonTitle()}
          position="bottom"
          noTooltip={!isPublishButtonDisabled()}
        >
          <button
            onClick={handleCampaignConfirm}
            disabled={isPublishButtonDisabled()}
            className="px-2.5 py-1.5 rounded-lg bg-[#0F31F7] text-white hover:bg-[#0F31F7] bg-opacity-90 transition-colors text-xs font-medium shadow-sm disabled:bg-gray disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#0F31F7]"
          >
            Publicar
          </button>
        </EsporaiTooltip>
      </MobileTopNavBar>
      <div className="flex flex-1 overflow-hidden relative">
        {/* Chat Section with responsive width */}
        <div className={`flex flex-col flex-1 min-w-0 transition-all bg-white duration-300 ease-in-out
          ${currentCampaign?.name && currentCampaign?.provider && !showMobilePreview ? 'lg:w-1/3 w-full' : 'w-full'}`}>
          {/* Messages Area with responsive padding, and extra bottom padding for mobile switcher */}
          <div className={`flex-1 overflow-y-auto ${showMobilePreview ? 'hidden lg:block' : 'block'} pb-24 sm:pb-6`} ref={chatRef}>
            {loadingChat ? (
              <div className="flex justify-center items-center h-full">
                <div className="flex flex-col items-center">
                  <RefreshCw className="w-8 h-8 text-[#0F31F7] animate-spin mb-4" />
                  <p className="text-sm text-gray-600">Cargando conversación...</p>
                </div>
              </div>
            ) : (
              <div className="max-w-3xl mx-auto p-3 sm:p-4 md:p-6">
                {messages.map((message, index) => (
                  <div key={index} className={`mb-4 ${message.role === 'assistant' ? 'pr-2 sm:pr-8' : 'pl-2 sm:pl-8'}`}>
                    <Message
                      message={message}
                      isTyping={false}
                    />
                  </div>
                ))}

                {showThinkingProcess && (
                  <div className="mb-4">
                    <ThinkingIndicator 
                      ref={thinkingRef}
                      onComplete={() => {
                        setShowThinkingProcess(false);
                        setIsTyping(true);
                      }}
                    />
                  </div>
                )}

                {isTyping && !showThinkingProcess && (
                  <div className="pr-2 sm:pr-8 mb-4">
                    <Message
                      message={{ role: 'assistant', content: '' }}
                      isTyping={true}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Input Area: always at the bottom, full width, above the switcher */}
          <div className={`px-3 sm:px-4 py-2 sm:py-3 bg-white ${showMobilePreview ? 'hidden lg:block' : 'block'}`} style={{ position: 'sticky', bottom: 0, zIndex: 20 }}>
            <div className="max-w-4xl mx-auto">
              {isCampaignSent ? (
                <div className="mb-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-900 text-center">
                  <span>Esta campaña ya fue creada. Para verla, ve a "Mis Campañas" en el menú de la izquierda.</span>
                </div>
              ) : (
                <>
                  {showQuickOptions && (messages.length === 0 || (messages.length === 1 && messages[0]?.role === 'assistant')) && !isTyping && !showThinkingProcess && (
                    <div className="mb-3">
                      <div className="w-full">
                        <QuickOptions
                          onSelect={handleQuickOptionSelect}
                        />
                      </div>
                    </div>
                  )}
                  <form onSubmit={handleSendMessage} className="flex items-center gap-2">
                    <textarea
                      ref={inputRef}
                      value={inputValue}
                      onChange={(e) => {
                        setInputValue(e.target.value);
                        handleInputResize(e);
                      }}
                      onInput={handleInputResize}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage(e);
                        }
                        // Shift+Enter will insert a new line by default
                      }}
                      rows={1}
                      disabled={Boolean(isTyping || loadingChat || showThinkingProcess)}
                      placeholder={(showThinkingProcess || isTyping) ? "Procesando..." : loadingChat ? "Cargando..." : "Describe tu campaña..."}
                      className="flex-1 h-14 sm:h-11 min-h-[3.5rem] sm:min-h-[2.75rem] max-h-[120px] px-4 py-3 bg-white rounded-xl
                        text-base sm:text-sm text-gray-900 placeholder-gray-500 
                        border-2 border-[#0F31F7] focus:outline-none focus:ring-2 focus:ring-[#0F31F7] focus:border-[#0F31F7]
                        transition-all duration-200 disabled:bg-gray-50 disabled:cursor-not-allowed
                        resize-none overflow-auto"
                      style={{ overflow: 'auto', resize: 'none' }}
                      autoFocus
                    />
                    <button
                      type="submit"
                      className="h-14 w-14 sm:h-11 sm:w-11 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-full
                        hover:from-blue-600 hover:to-blue-700 transition-all duration-200 
                        disabled:opacity-50 disabled:hover:from-blue-500 disabled:hover:to-blue-600
                        flex items-center justify-center text-lg shadow-lg hover:shadow-xl transform hover:scale-105"
                      disabled={isTyping || !inputValue.trim() || loadingChat || showThinkingProcess}
                    >
                      {loadingResponse ? (
                        <Loader className="w-5 h-5 animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Desktop Preview Section */}
        <AnimatePresence>
          {currentCampaign?.name && currentCampaign?.provider && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: '66.666667%', opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              className={`hidden lg:flex lg:flex-col h-full bg-white border-l border-gray-200 lg:block`}
            >
              <PreviewHeader
                onClose={() => setCurrentCampaign(null)}
                isMetaCampaign={currentCampaign?.provider?.toLowerCase().includes('meta')}
                hasMedia={currentCampaign?.ad_content?.media?.images?.length > 0 || currentCampaign?.ad_content?.media?.videos?.length > 0}
              />
              <div className="flex-1 overflow-y-auto">
                <div className="px-4 pb-4 sm:px-6">
                  <CampaignPreviewSection
                    campaignPreview={currentCampaign}
                    deviceView={deviceView}
                    activeTab={activeTab}
                    isCampaignReady={isCampaignReady}
                    onConfirm={handleCampaignConfirm}
                    setCampaignPreview={setCurrentCampaign}
                    auth={auth}
                    isCreatingCampaign={isCreatingCampaign}
                    API_URL={API_URL}
                    chatId={localChatId}
                    onGoogleValidationChange={setIsGoogleValid}
                  />
                </div>
              </div>
              
              {/* Footer with Publish Button */}
              {isCampaignReady && (
                <div className="border-t border-gray-200 bg-white px-3 sm:px-4 py-2 sm:py-3">
                  <div className="flex justify-center">
                    {Boolean(currentCampaign?.provider_campaign_id) ? (
                      <button
                        onClick={() => {
                          const providerId = currentCampaign.provider_campaign_id;
                          const url = `/campaigns/${currentCampaign.provider}/${providerId}`;
                          navigate(url);
                        }}
                        className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 
                          transition-all duration-300 flex items-center gap-2 text-sm font-medium
                          animate-publishReady hover:animate-publishGlow
                          transform hover:scale-105 active:scale-95
                          shadow-lg hover:shadow-xl min-w-[140px] justify-center"
                      >
                        <ExternalLink className="w-4 h-4" />
                        <span>Ver Campaña</span>
                      </button>
                    ) : (
                      <EsporaiTooltip
                        content={getDisabledButtonTitle()}
                        position="top"
                        noTooltip={!isPublishButtonDisabled()}
                      >
                        <button
                          onClick={() => {
                            createLog({
                              level: 'info',
                              message: 'Click en "Publicar campaña" en footer preview'
                            });
                            handleCampaignConfirm();
                          }}
                          disabled={isPublishButtonDisabled()}
                          className={`px-6 py-3 rounded-xl transition-all duration-300 flex items-center gap-2 
                            text-sm font-medium min-w-[140px] justify-center
                            ${isPublishButtonDisabled() 
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                              : 'bg-blue-600 text-white hover:bg-blue-700 animate-publishReady animate-publishPulse hover:animate-publishGlow transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl'
                            }`}
                        >
                          {isCreatingCampaign ? (
                            <>
                              <Loader className="w-4 h-4 animate-spin" />
                              <span>Creando...</span>
                            </>
                          ) : (
                            <>
                              <Check className="w-4 h-4" />
                              <span>¡Publicar!</span>
                            </>
                          )}
                        </button>
                      </EsporaiTooltip>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile/Tablet Full-Screen Preview */}
        <AnimatePresence>
          {currentCampaign?.name && currentCampaign?.provider && showMobilePreview && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-10 lg:hidden flex flex-col bg-white"
            >
              <div className="flex-1 overflow-y-auto">
                <div className="p-3 sm:p-4">
                  <CampaignPreviewSection
                    campaignPreview={currentCampaign}
                    deviceView="mobile" // Force mobile view on small screens
                    activeTab={activeTab}
                    isCampaignReady={isCampaignReady}
                    onConfirm={handleCampaignConfirm}
                    setCampaignPreview={setCurrentCampaign}
                    auth={auth}
                    isCreatingCampaign={isCreatingCampaign}
                    API_URL={API_URL}
                    chatId={localChatId}
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>


      {/* Mobile Switcher: Always fixed at the absolute bottom on mobile, above any footer menu */}
      <div className="fixed bottom-0 left-0 right-0 w-full lg:hidden z-[70] bg-white border-t border-gray-200">
        <div className="flex items-stretch">
          <button
            onClick={handleNewChat}
            aria-label="Nuevo chat"
            className="w-12 flex items-center justify-center py-3 text-[#0F31F7] border-t-2 border-transparent bg-white hover:bg-blue-50 transition-colors"
          >
            <PlusCircle className="w-6 h-6" />
          </button>
          <button
            onClick={() => setShowMobilePreview(false)}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              !showMobilePreview 
                ? 'text-[#0F31F7] border-t-2 border-[#0F31F7] bg-blue-50' 
                : 'text-gray-600 border-t-2 border-transparent bg-white hover:bg-gray-50'
            }`}
          >
            Chat
          </button>
          <button
            onClick={() => setShowMobilePreview(true)}
            disabled={!currentCampaign?.name || !currentCampaign?.provider}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              showMobilePreview 
                ? 'text-[#0F31F7] border-t-2 border-[#0F31F7] bg-blue-50' 
                : 'text-gray-600 border-t-2 border-transparent bg-white hover:bg-gray-50'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            Previsualizar
          </button>
        </div>
      </div>

      {/* Success Animation */}
      <SuccessAnimation
        isVisible={showSuccessAnimation}
        onComplete={handleSuccessAnimationComplete}
        campaignProvider={successCampaignData?.provider}
        providerId={successCampaignData?.providerId}
      />

      {/* Add extra bottom padding to chat container for mobile */}
    </div>
  );
};
export default AICampaignCreation;
